/**
 * Promise-based text/select prompt provider.
 *
 * Replaces window.prompt() with a branded modal consistent with ConfirmProvider.
 *
 *   const prompt = usePrompt();
 *   const name = await prompt({
 *     title: 'Sign report',
 *     body: 'Type your name to submit:',
 *     required: true,
 *   });
 *   if (name === null) return;
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import PromptModal from './PromptModal';

const PromptContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: 'Input required',
  body: '',
  label: '',
  defaultValue: '',
  placeholder: '',
  required: true,
  multiline: false,
  inputType: 'text',
  options: [],
  submitText: 'OK',
  cancelText: 'Cancel',
};

export function PromptProvider({ children }) {
  const [state, setState] = useState({ show: false, options: DEFAULT_OPTIONS });
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    setState((prev) => ({ ...prev, show: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const prompt = useCallback((options = {}) => {
    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(null);
      }
      resolverRef.current = resolve;
      setState({ show: true, options: { ...DEFAULT_OPTIONS, ...options } });
    });
  }, []);

  const { show, options } = state;

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      <PromptModal
        show={show}
        onHide={() => settle(null)}
        onSubmit={(value) => settle(value)}
        title={options.title}
        body={options.body}
        label={options.label}
        defaultValue={options.defaultValue}
        placeholder={options.placeholder}
        required={options.required}
        multiline={options.multiline}
        inputType={options.inputType}
        options={options.options}
        submitText={options.submitText}
        cancelText={options.cancelText}
      />
    </PromptContext.Provider>
  );
}

/**
 * Returns prompt(options) => Promise<string | null>.
 * Falls back to window.prompt when used outside PromptProvider.
 */
export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (ctx) return ctx;
  return (options = {}) => {
    const message = [options.title, options.body, options.label]
      .filter(Boolean)
      .join('\n\n');
    const result = window.prompt(message || 'Enter value:', options.defaultValue ?? '');
    if (result === null) return Promise.resolve(null);
    if (options.required && !String(result).trim()) return Promise.resolve(null);
    return Promise.resolve(result);
  };
}

export default PromptProvider;
